import React, { useState, useEffect, useRef } from 'react';
import { UnscheduledEvent } from '../types';
import { FCP_CODES } from '../constants';
import { EVENT_STATUS } from '../constants';
import { extractTime } from './eventModal/dateTimeUtils';
import { aliasFromEmail } from './eventModal/saveHelpers';
import { runAutoAuditRules, findingKeyForEntry } from './auditRules';
import { useAuth } from '../auth';
import AuditEventSummary from './AuditEventSummary';
import AuditHistoryList from './AuditHistoryList';
import { PAPERWORK_WAIT_PATTERN } from './auditPaperwork';

/**
 * A single audit note entry.
 */
export interface AuditNoteEntry {
  id: string;
  date: string;
  time: string;
  alias: string;
  fcpCode: string;
  text: string;
  /** Alias of the user who last edited this entry (set on edit). */
  editedBy?: string;
}

/**
 * Props for the AuditModal component.
 */
interface AuditModalProps {
  event: UnscheduledEvent;
  isOpen: boolean;
  onClose: () => void;
  onAuditComplete?: () => void;
  barriers?: { id: string; type: string; startTime: string; endTime: string; notes: string }[];
  auditEntries: AuditNoteEntry[];
  onAuditEntriesChange: (entries: AuditNoteEntry[]) => void;
  /** Tombstoned auto-audit finding keys (deleted findings, never re-logged). */
  deletedFindings?: string[];
  onDeletedFindingsChange?: (keys: string[]) => void;
  noteEntries?: {
    id: string;
    date: string;
    time: string;
    alias: string;
    defectIds: number[];
    text: string;
  }[];
  /**
   * Surfaces the Auto-Audit button even when the record's live status is not yet
   * COMPLETED — e.g. while moving an event (which closes the source as
   * COMPLETED). Running Auto-Audit satisfies the audit gate, turning the Audit
   * button blue and activating Update for the completion/move.
   */
  auditActive?: boolean;
  /**
   * Waiting Paperwork (date + Zulu time) is owned by EventModal so it survives
   * this popup closing and is captured on the event save (snapshotted onto each
   * audit finding). This modal reads and updates it via these props.
   */
  waitingPaperworkDate: string;
  waitingPaperworkTime: string;
  setWaitingPaperworkDate: (value: string) => void;
  setWaitingPaperworkTime: (value: string) => void;
}

/**
 * Returns the current UTC date as YYYY-MM-DD.
 */
function getCurrentDateZulu(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Returns the current UTC time as HH:MM.
 */
function getCurrentTimeZulu(): string {
  const now = new Date();
  return `${String(now.getUTCHours()).padStart(2, '0')}:${String(now.getUTCMinutes()).padStart(2, '0')}`;
}

/**
 * Audit popup modal displaying read-only event data
 * and editable fields for Waiting Paperwork, FCP Code, and Notes.
 * Includes automated audit rules triggered via the Auto-Audit Features button.
 */
const AuditModal: React.FC<AuditModalProps> = ({
  event,
  isOpen,
  onClose,
  onAuditComplete,
  barriers: currentBarriers,
  auditEntries,
  onAuditEntriesChange,
  deletedFindings = [],
  onDeletedFindingsChange,
  noteEntries,
  auditActive = false,
  waitingPaperworkDate,
  waitingPaperworkTime,
  setWaitingPaperworkDate,
  setWaitingPaperworkTime,
}) => {
  const { user } = useAuth();
  const userAlias = aliasFromEmail(user?.email) || '—';
  const [fcpCode, setFcpCode] = useState<string>('');
  const [noteText, setNoteText] = useState<string>('');
  const [noteTimestamp, setNoteTimestamp] = useState<{ date: string; time: string }>({
    date: '',
    time: '',
  });
  // Id of the audit entry currently being edited (null when adding a new one).
  const [editingId, setEditingId] = useState<string | null>(null);
  // Ensures the Waiting Paperwork auto-populate runs at most once per open so it
  // never clobbers a value the user has manually edited.
  const autoPopulatedRef = useRef<boolean>(false);

  // When FCP Code is selected, capture the current timestamp for the note.
  // Skipped while editing an existing entry (its original timestamp is kept).
  useEffect(() => {
    if (editingId) return;
    if (fcpCode) {
      setNoteTimestamp({ date: getCurrentDateZulu(), time: getCurrentTimeZulu() });
    } else {
      setNoteText('');
      setNoteTimestamp({ date: '', time: '' });
    }
  }, [fcpCode, editingId]);

  // Reset input state when modal closes (but NOT audit entries — those persist)
  useEffect(() => {
    if (!isOpen) {
      // Waiting Paperwork is owned by EventModal and intentionally NOT reset
      // here, so it survives this popup closing and is captured on the save.
      setFcpCode('');
      setNoteText('');
      setEditingId(null);
      autoPopulatedRef.current = false;
    }
  }, [isOpen]);

  // Auto-populate Waiting Paperwork from note entries on open. Runs once per
  // open (autoPopulatedRef) and never overwrites a value already present, so the
  // field stays freely editable after auto-population.
  useEffect(() => {
    if (!isOpen || autoPopulatedRef.current) return;
    if (!noteEntries || noteEntries.length === 0) return;
    if (waitingPaperworkDate && waitingPaperworkTime) return;
    for (const note of noteEntries) {
      if (PAPERWORK_WAIT_PATTERN.test(note.text)) {
        setWaitingPaperworkDate(note.date);
        setWaitingPaperworkTime(note.time);
        autoPopulatedRef.current = true;
        break;
      }
    }
  }, [isOpen, noteEntries, waitingPaperworkDate, waitingPaperworkTime]);

  /**
   * Runs all automated audit rules via the shared rule engine and appends any
   * new SYSTEM entries. Each rule de-duplicates against existing entries.
   */
  const handleAutoAudit = (): void => {
    // Rule 5: when the event is Completed, each auto-generated FCP entry is
    // stamped with the ERTS date/time rather than the current time. Falls back
    // to the current UTC time when no usable ERTS is present.
    const ertsRaw = event.returnToServiceTimeZulu || '';
    const ertsDate = ertsRaw.split(/[ T]/)[0] || '';
    const ertsTime = extractTime(ertsRaw);
    const useErts = event.status === EVENT_STATUS.COMPLETED && !!ertsDate && ertsTime.length >= 4;

    const newEntries = runAutoAuditRules({
      event,
      existingEntries: auditEntries,
      waitingPaperworkDate,
      waitingPaperworkTime,
      barriers: currentBarriers || event.barriers || [],
      logDate: useErts ? ertsDate : getCurrentDateZulu(),
      logTime: useErts ? ertsTime : getCurrentTimeZulu(),
      deletedFindings,
    });

    if (newEntries.length > 0) {
      onAuditEntriesChange([...auditEntries, ...newEntries]);
    }

    if (onAuditComplete) {
      onAuditComplete();
    }
  };

  if (!isOpen) return null;

  const handleSubmitNote = (): void => {
    if (!noteText.trim() || !fcpCode) return;
    if (editingId) {
      const updated = auditEntries.map((entry) =>
        entry.id === editingId
          ? { ...entry, fcpCode, text: noteText.trim(), editedBy: userAlias }
          : entry,
      );
      onAuditEntriesChange(updated);
      setEditingId(null);
    } else {
      const newNote: AuditNoteEntry = {
        id: `audit-note-${Date.now()}`,
        date: noteTimestamp.date,
        time: noteTimestamp.time,
        alias: userAlias,
        fcpCode,
        text: noteText.trim(),
      };
      onAuditEntriesChange([...auditEntries, newNote]);
    }
    setNoteText('');
    setFcpCode('');
  };

  /** Loads an entry into the FCP Code + Notes fields for editing. */
  const handleEditEntry = (entry: AuditNoteEntry): void => {
    setEditingId(entry.id);
    setFcpCode(entry.fcpCode);
    setNoteText(entry.text);
    setNoteTimestamp({ date: entry.date, time: entry.time });
  };

  /** Removes an entry; cancels edit mode if the edited entry is removed. */
  const handleDeleteEntry = (entry: AuditNoteEntry): void => {
    onAuditEntriesChange(auditEntries.filter((e) => e.id !== entry.id));
    // Tombstone deleted SYSTEM (auto-audit) findings so neither the manual
    // Auto-Audit nor the nightly job re-logs them.
    if (entry.alias === 'SYSTEM' && onDeletedFindingsChange) {
      const key = findingKeyForEntry(entry);
      if (!deletedFindings.includes(key)) {
        onDeletedFindingsChange([...deletedFindings, key]);
      }
    }
    if (editingId === entry.id) {
      setEditingId(null);
      setFcpCode('');
      setNoteText('');
    }
  };

  /** Cancels an in-progress edit without applying changes. */
  const handleCancelEdit = (): void => {
    setEditingId(null);
    setFcpCode('');
    setNoteText('');
  };

  return (
    <div className="modal-overlay active" role="dialog" aria-modal="true" aria-label="Audit">
      <div className="modal audit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Audit</h2>
          <button className="modal-close" onClick={onClose} type="button" aria-label="Close">
            &times;
          </button>
        </div>

        <div className="modal-body-scroll">
          {/* Read-only fields from main record */}
          <AuditEventSummary event={event} />

          {/* Editable: Waiting Paperwork Date and Time */}
          <div className="field-row">
            <label className="field-label">Waiting Paperwork</label>
            <div className="field-group">
              <div className="field-input-block">
                <span className="field-input-label">Date</span>
                <input
                  type="date"
                  value={waitingPaperworkDate}
                  onChange={(e) => setWaitingPaperworkDate(e.target.value)}
                  className="field-input"
                />
              </div>
              <div className="field-input-block">
                <span className="field-input-label">Time (Z)</span>
                <input
                  type="time"
                  value={waitingPaperworkTime}
                  onChange={(e) => setWaitingPaperworkTime(e.target.value)}
                  className="field-input"
                />
              </div>
            </div>
          </div>

          {/* FCP Code Dropdown */}
          <div className="field-row">
            <label className="field-label">FCP Code</label>
            <div className="field-group">
              <select
                value={fcpCode}
                onChange={(e) => setFcpCode(e.target.value)}
                className="field-input audit-fcp-select"
              >
                <option value="">Select FCP Code...</option>
                {FCP_CODES.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes section — visible when FCP Code is selected */}
          {fcpCode && (
            <div className="field-row field-row-tall">
              <label className="field-label">Notes</label>
              <div className="field-group defect-group">
                <div className="audit-note-meta">
                  <div className="audit-note-meta-row">
                    <span className="field-input-label">Date &amp; Time</span>
                    <span className="note-timestamp">
                      {noteTimestamp.date} {noteTimestamp.time}z
                    </span>
                  </div>
                  <div className="audit-note-meta-row">
                    <span className="field-input-label">Logged By</span>
                    <span className="note-alias">{userAlias}</span>
                  </div>
                  {editingId && (
                    <div className="audit-note-meta-row">
                      <span className="field-input-label">Edited By</span>
                      <span className="note-alias">{userAlias}</span>
                    </div>
                  )}
                </div>
                <textarea
                  className="field-textarea"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={4}
                  placeholder="Enter audit notes..."
                />
                <div className="audit-note-actions">
                  {editingId && (
                    <button
                      className="modal-btn modal-btn-cancel"
                      type="button"
                      onClick={handleCancelEdit}
                    >
                      Cancel Edit
                    </button>
                  )}
                  <button
                    className="modal-btn modal-btn-save audit-note-submit-btn"
                    type="button"
                    disabled={!noteText.trim()}
                    onClick={handleSubmitNote}
                  >
                    {editingId ? 'Update Note' : 'Submit Note'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Audit History — includes both auto-logged and manual entries */}
          <AuditHistoryList
            entries={auditEntries}
            onEdit={handleEditEntry}
            onDelete={handleDeleteEntry}
          />
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="modal-btn modal-btn-cancel" type="button" onClick={onClose}>
            Close
          </button>
          {(auditActive || event.status === EVENT_STATUS.COMPLETED) && (
            <button
              className="modal-btn modal-btn-auto-audit"
              type="button"
              onClick={handleAutoAudit}
            >
              Auto-Audit
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditModal;
